import { useEffect, useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { __ } from '@wordpress/i18n';
import BadgesSkeleton from './BadgesSkeleton';
import { BadgeCheck } from 'lucide-react';
import { NoInformation } from '@dokan/components';

interface Badge {
    id: number;
    badge_logo: string;
    badge_name: string;
    formatted_hover_text?: string;
    event?: {
        has_multiple_levels?: boolean;
        group?: {
            key?: string;
            title?: string;
        };
    };
    acquired_level_count?: number;
    level_count?: number;
    vendor_count?: number;
}

const BadgesTab = ( { vendor } ) => {
    const [ badges, setBadges ] = useState< Badge[] >( [] );
    const [ loading, setLoading ] = useState( false );

    useEffect( () => {
        if ( ! vendor?.id ) return;
        setLoading( true );
        apiFetch( {
            path: addQueryArgs( '/dokan/v1/seller-badge', {
                page: 1,
                per_page: -1,
                vendor_id: vendor.id,
                badge_status: 'published',
            } ),
        } )
            .then( ( response ) => {
                setBadges(
                    Array.isArray( response )
                        ? response.filter(
                              ( item: Badge ) =>
                                  item.vendor_count && item.vendor_count > 0
                          )
                        : []
                );
            } )
            .catch( () => setBadges( [] ) )
            .finally( () => setLoading( false ) );
    }, [ vendor ] );

    // Group badges by group key instead of title
    const groupedBadges: Record< string, Badge[] > = badges.reduce(
        ( groups: Record< string, Badge[] >, badge: Badge ) => {
            const groupKey = badge?.event?.group?.key || 'other';
            if ( ! groups[ groupKey ] ) {
                groups[ groupKey ] = [];
            }
            groups[ groupKey ].push( badge );
            return groups;
        },
        {}
    );

    // Define group titles based on group keys
    const getGroupTitle = ( groupKey: string ): string => {
        const groupTitles: Record< string, string > = {
            product: __( 'Product Related Badges', 'dokan' ),
            order: __( 'Order Related Badges', 'dokan' ),
            vendor: __( 'Seller Related Badges', 'dokan' ),
            customer_basis: __( 'Customer Related Badges', 'dokan' ),
            amount: __( 'Sale Amount Related Badges', 'dokan' ),
        };
        return groupTitles[ groupKey ] || __( 'Other Badges', 'dokan' );
    };

    if ( loading ) {
        return <BadgesSkeleton />;
    }

    return (
        <>
            { badges.length ? (
                <div className="dokan-layout bg-white px-8 py-6 rounded shadow-md">
                    <div className="badges-container">
                        { Object.entries( groupedBadges ).map(
                            ( [ groupKey, groupBadges ] ) => (
                                <div key={ groupKey } className="mb-10">
                                    <div className="relative mb-8">
                                        <h4 className="text-lg font-medium text-gray-600 mb-4">
                                            { getGroupTitle( groupKey ) }
                                        </h4>
                                        <div className="w-full h-px bg-gray-200 relative">
                                            <div className="absolute left-0 top-0 h-px transition-all duration-300 ease-in-out w-16 hover:w-32"></div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                                        { ( groupBadges as Badge[] ).map(
                                            ( badge, index ) => (
                                                <div
                                                    className="flex items-center space-x-3"
                                                    key={ index }
                                                >
                                                    <div className="relative">
                                                        <img
                                                            src={
                                                                badge.badge_logo
                                                            }
                                                            className="w-12 h-12 object-contain"
                                                            title={
                                                                badge.formatted_hover_text
                                                            }
                                                            alt={
                                                                badge.badge_name
                                                            }
                                                            style={ {
                                                                cursor: 'pointer',
                                                            } }
                                                        />
                                                        { badge.event
                                                            ?.has_multiple_levels &&
                                                            badge.acquired_level_count !==
                                                                undefined && (
                                                                <span
                                                                    className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1/3 w-6 h-6 bg-white text-gray-800 rounded-full flex items-center justify-center text-xs font-bold shadow-xl border border-gray-200"
                                                                    title={ __(
                                                                        `Level: ${ badge.acquired_level_count } of ${ badge.level_count }`,
                                                                        'dokan'
                                                                    ) }
                                                                >
                                                                    {
                                                                        badge.acquired_level_count
                                                                    }
                                                                </span>
                                                            ) }
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-normal text-[#393939] text-lg">
                                                            { badge.badge_name }
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        ) }
                                    </div>
                                </div>
                            )
                        ) }
                    </div>
                </div>
            ) : (
                <NoInformation
                    icon={ <BadgeCheck /> }
                    description={ __(
                        'Badges will appear once acquired',
                        'dokan'
                    ) }
                    title={ __( 'No Badge Acquired', 'dokan' ) }
                />
            ) }
        </>
    );
};

export default BadgesTab;
